import {
    AfterViewInit,
    Component,
    EventEmitter,
    inject,
    OnInit,
} from '@angular/core';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { TableDataItem } from 'src/app/components/table-dashboard-item/table-dashboard-item.component';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { FlowDashboardStateService } from 'src/app/services/flow-dashboard/flow-dashboard-state.service';
import {
    FlowAxis,
    getFlowAxis,
} from 'src/app/services/flow-dashboard/flow-dashboard.models';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { DatasourceColumnOldDataLoaderFactoryOptions } from '../common/data-loaders/datasource-column-old';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { createFlowBreakdownTableDashboardItem } from '../common/items/flow-breakdown-table';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { ICheckboxSettingDashboardItem } from '../common/items/setting/checkbox/checkbox.component';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { BiVizDashboardDecoActivitySqlMap } from '../mopa-resources-detailed/sql-maps/biviz-dashboard-deco-activity-datasource-sql-map';
import { DecoFlowByDateDataLoaderFactory } from './data-loaders/deco-flow-by-date';
import { DecoFlowByDayNoDataLoaderFactory } from './data-loaders/deco-flow-by-day-no';
import { DecoFlowKpiDataLoaderFactory } from './data-loaders/deco-flow-kpi';
import {
    DECO_FLOW_POPULATIONS,
    DECO_FLOW_VALUE_MODE_OPTIONS,
    getDecoFlowStatistic,
} from './deco-flow.models';
import { MopaDecoFlowChartDocs } from './mopa-deco-flow.chart-documentation';

interface DecoFlowFilterSection {
    title: string;
    accent: 'chart' | 'activity' | 'audience' | 'context';
    columnIds: DatasourceColumnId[];
    includesMetricSettings?: boolean;
}

/** Deco Flow compares gains with first placements from `dashboard_deco_activity`. */
@Component({
    selector: 'app-mopa-deco-flow',
    templateUrl: './mopa-deco-flow.component.html',
    styleUrls: ['./mopa-deco-flow.component.scss'],
    providers: [
        DashboardService,
        QueryContextService,
        CommonLegendService,
        FlowDashboardStateService,
    ],
})
export class MopaDecoFlowComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Deco Flow';
    override DASHBOARD_ID = 'mopa-deco-flow';
    override dashboardDocumentation: string = MopaDecoFlowChartDocs.dashboard;

    private gcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    /////////////////////////////////////////////////////////////////
    override filterDataLoaders = {
        'mopa.deco-flow.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ](
            new SegmentDatasourceColumnDataLoaderFactoryOptions(
                'mopa.deco-flow.filter_segments',
                this.gcpProjectId,
                this.queryCache
            )
        ).create(),
        'mopa.deco-flow.filter.resources_source': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.deco-flow.filter.resources_source',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'RESOURCES_SOURCE',
                datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
                filterContext: true,
            })
        ).create(),
        'mopa.deco-flow.filter.resource_payment_type': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.deco-flow.filter.resource_payment_type',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'RESOURCE_PAYMENT_TYPE',
                datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'RESOURCE_PAYMENT_TYPE'
                ),
            })
        ).create(),
        'mopa.deco-flow.filter.deco_context_rarity': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.deco-flow.filter.deco_context_rarity',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'DECO_CONTEXT_RARITY',
            datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery('DECO_CONTEXT_RARITY'),
        }).create(),
        'mopa.deco-flow.filter.deco_context_size': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.deco-flow.filter.deco_context_size',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'DECO_CONTEXT_SIZE',
            datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery('DECO_CONTEXT_SIZE'),
        }).create(),
        'mopa.deco-flow.filter.deco_context_set_id': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.deco-flow.filter.deco_context_set_id',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'DECO_CONTEXT_SET_ID',
            datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.deco-flow.filter.deco_context_inspirations': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.deco-flow.filter.deco_context_inspirations',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'DECO_CONTEXT_INSPIRATIONS',
            datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery(
                'DECO_CONTEXT_INSPIRATIONS'
            ),
        }).create(),
        'mopa.deco-flow.filter.install_day_no': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.deco-flow.filter.install_day_no',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'INSTALL_DAY_NO',
            datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery('INSTALL_DAY_NO'),
        }).create(),
        'mopa.deco-flow.filter.activity_app_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.deco-flow.filter.activity_app_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.deco-flow.filter.activity_app_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.deco-flow.filter.activity_app_version',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_APP_VERSION',
            datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('ACTIVITY_APP_VERSION'),
        }).create(),
        'mopa.deco-flow.filter.firebase_app_store': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.deco-flow.filter.firebase_app_store',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_APP_STORE',
                datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
            })
        ).create(),
        'mopa.deco-flow.filter.firebase_country_european_union':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.deco-flow.filter.firebase_country_european_union',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
            }).create(),
        'mopa.deco-flow.filter.firebase_country_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.deco-flow.filter.firebase_country_tier',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
            datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
        }).create(),
        'mopa.deco-flow.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.deco-flow.filter.firebase_country',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
        }).create(),
        'mopa.deco-flow.filter.attribution_traffic_source': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.deco-flow.filter.attribution_traffic_source',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
            })
        ).create(),
        'mopa.deco-flow.filter.attribution_network_name': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.deco-flow.filter.attribution_network_name',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
                datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
            })
        ).create(),
        'mopa.deco-flow.filter.attribution_campaign_name': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.deco-flow.filter.attribution_campaign_name',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
                filterContext: true,
            })
        ).create(),
        'mopa.deco-flow.filter.firebase_install_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.deco-flow.filter.firebase_install_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.deco-flow.filter.firebase_install_version': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.deco-flow.filter.firebase_install_version',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
                datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION'
                ),
            })
        ).create(),
        'mopa.deco-flow.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.deco-flow.filter.etl_payer',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_PAYER',
            datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
        }).create(),
        'mopa.deco-flow.filter.etl_revenue_badge': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.deco-flow.filter.etl_revenue_badge',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ETL_REVENUE_BADGE',
                datasourceSqlMap: BiVizDashboardDecoActivitySqlMap.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery('ETL_REVENUE_BADGE'),
            })
        ).create(),
    };

    override chartDataLoaders = {
        'mopa.deco-flow.modify_time_deco_activity_datasource':
            DataLoaderFactories['common.modify_time']({
                createdInstanceId:
                    'mopa.deco-flow.modify_time_deco_activity_datasource',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasetId: 'bi_viz',
                tableId: 'dashboard_deco_activity',
            }).create(),
        'mopa.deco-flow.by_date': new DecoFlowByDateDataLoaderFactory({
            createdInstanceId: 'mopa.deco-flow.by_date',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
        'mopa.deco-flow.kpi': new DecoFlowKpiDataLoaderFactory({
            createdInstanceId: 'mopa.deco-flow.kpi',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
        'mopa.deco-flow.by_day_no': new DecoFlowByDayNoDataLoaderFactory({
            createdInstanceId: 'mopa.deco-flow.by_day_no',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////

    xAxisSetting: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.FLOW_X_AXIS,
            viewtype: 'toggle',
            displayName: 'X axis',
            value: 'date',
            selectOptions: ['date', 'install_day', 'active_day'],
        },
    };
    accumulationSetting: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.FLOW_ACCUMULATION,
            viewtype: 'toggle',
            displayName: 'Accumulation',
            value: 'daily',
            selectOptions: ['daily', 'running'],
        },
    };
    valueModeSetting: ISingleSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.FLOW_VALUE_MODE,
            viewtype: 'singleSelect',
            displayName: 'Value mode',
            description: `Statistic calculated for each side of the flow (gained ▲ and placed ▼) separately: TOTAL, AVERAGE, MEDIAN / PERCENTILE, FLOW RECORDS, PLAYERS.

P50 / P75 / P90 / P99 work for MEDIAN / PERCENTILE only.

Definitions and formulas of every mode are in the chart documentation.`,
            value: 'total',
            selectOptions: [...DECO_FLOW_VALUE_MODE_OPTIONS],
        },
    };
    populationSetting: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.FLOW_POPULATION,
            viewtype: 'toggle',
            displayName: 'Population',
            description: `Denominator of the selected statistic: DEU (only players with a positive flow on that side) or DAU (every active player).

Works for AVERAGE and MEDIAN / PERCENTILE only.`,
            value: 'deu',
            selectOptions: [...DECO_FLOW_POPULATIONS],
        },
    };
    settingShowP50: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_PERCENTILE_50,
            viewtype: 'checkbox',
            displayName: 'P50',
            value: true,
        },
    };
    settingShowP75: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_PERCENTILE_75,
            viewtype: 'checkbox',
            displayName: 'P75',
            value: false,
        },
    };
    settingShowP90: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_PERCENTILE_90,
            viewtype: 'checkbox',
            displayName: 'P90',
            value: false,
        },
    };
    settingShowP99: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_PERCENTILE_99,
            viewtype: 'checkbox',
            displayName: 'P99',
            value: false,
        },
    };

    // CHARTS
    flowState = inject(FlowDashboardStateService);

    flowChart: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.deco-flow.flow',
        dashboardItemFactory: DashboardItemFactories['common.chart.flow_chart'](
            {
                createdInstanceId: 'mopa.deco-flow.flow',
                dashboardName: this.DASHBOARD_NAME,
                title: 'Decorations gained ▲ / New decos placed ▼',
                chartDocumentation: MopaDecoFlowChartDocs.dashboard,
                variant: 'flow',
                onChartInstance: (e) => this.flowState.ensureWired(e, 'flow'),
            }
        ),
        dataLoaderEmitter: this.flowState.flowViewModel$,
    };
    netChart: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.deco-flow.net',
        dashboardItemFactory: DashboardItemFactories['common.chart.flow_chart'](
            {
                createdInstanceId: 'mopa.deco-flow.net',
                dashboardName: this.DASHBOARD_NAME,
                title: 'Net (gained - placed)',
                variant: 'net',
                onChartInstance: (e) => this.flowState.ensureWired(e, 'net'),
            }
        ),
        dataLoaderEmitter: this.flowState.netViewModel$,
    };

    kpiTiles: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.deco-flow.kpi_tiles',
        dashboardItemFactory: DashboardItemFactories['common.kpi_tiles']({
            createdInstanceId: 'mopa.deco-flow.kpi_tiles',
            composeData: (tiles) => {
                const documentation = `
<p><strong>Affected players</strong> - the share of the active population that the selected statistic actually covers, over the whole loaded date range.</p>
<ul>
  <li><strong>Denominator</strong> - players active at least once in the range, counted once per player (DAU source).</li>
  <li><strong>Numerator</strong> - active players who gained or placed at least one decoration in the range. For per-DAU metrics, the statistic covers the whole active population, so the tile reads 100%.</li>
  <li><strong>Value</strong> - numerator / denominator; the caption under the value repeats the numerator as a player count.</li>
</ul>
<p>Read it as coverage of the headline value: a low share means Gained / Placed / Net describe a small slice of the active players. Like the other KPI tiles it comes from one range-wide query - the sidebar filters apply, while the chart's Split, Group and zoom are deliberately ignored.</p>
`;
                return tiles.map((tile) =>
                    tile.label === 'Affected players'
                        ? { ...tile, documentation }
                        : tile
                );
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['mopa.deco-flow.kpi'].emitter,
    };

    modify_time_deco_activity_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.deco-flow.modify_time_deco_activity_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.deco-flow.modify_time_deco_activity_datasource',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.deco-flow.modify_time_deco_activity_datasource'
            ].emitter,
    };

    // Breakdown tables mirror the legend.
    readonly breakdowns = [
        {
            direction: 'in' as const,
            ariaLabel: 'Decorations gained legend controls',
            dashboardItem: createFlowBreakdownTableDashboardItem({
                dashboardName: this.DASHBOARD_NAME,
                instanceId: 'mopa.deco-flow.inflow-breakdown',
                title: '▲ Decorations gained',
            }),
            dataLoaderEmitter: this.flowState.inflowRows$,
        },
        {
            direction: 'out' as const,
            ariaLabel: 'New decos placed legend controls',
            dashboardItem: createFlowBreakdownTableDashboardItem({
                dashboardName: this.DASHBOARD_NAME,
                instanceId: 'mopa.deco-flow.outflow-breakdown',
                title: '▼ New decos placed',
            }),
            dataLoaderEmitter: this.flowState.outflowRows$,
        },
    ];

    // FILTERS: sidebar order; categories are splitable by default.
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        DECO_CONTEXT_RARITY: {
            id: 'mopa.deco-flow.filter.deco_context_rarity',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.deco-flow.filter.deco_context_rarity',
                datasourceColumnId: 'DECO_CONTEXT_RARITY',
                bucketable: true,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.deco_context_rarity'
                ].emitter,
        },
        DECO_CONTEXT_SIZE: {
            id: 'mopa.deco-flow.filter.deco_context_size',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.deco-flow.filter.deco_context_size',
                datasourceColumnId: 'DECO_CONTEXT_SIZE',
                bucketable: true,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.deco_context_size'
                ].emitter,
        },
        DECO_CONTEXT_SET_ID: {
            id: 'mopa.deco-flow.filter.deco_context_set_id',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.deco_context_set_id',
                    'DECO_CONTEXT_SET_ID'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.deco_context_set_id'
                ].emitter,
        },
        DECO_CONTEXT_INSPIRATIONS: {
            id: 'mopa.deco-flow.filter.deco_context_inspirations',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.deco_context_inspirations',
                    'DECO_CONTEXT_INSPIRATIONS'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.deco_context_inspirations'
                ].emitter,
        },
        RESOURCES_SOURCE: {
            id: 'mopa.deco-flow.filter.resources_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.resources_source',
                    'RESOURCES_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.deco-flow.filter.resources_source']
                    .emitter,
        },
        RESOURCE_PAYMENT_TYPE: {
            id: 'mopa.deco-flow.filter.resource_payment_type',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.deco-flow.filter.resource_payment_type',
                datasourceColumnId: 'RESOURCE_PAYMENT_TYPE',
                bucketable: true,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.resource_payment_type'
                ].emitter,
        },
        ACTIVITY_DATE: {
            id: 'mopa.deco-flow.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'mopa.deco-flow.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
                splitable: false,
            }),
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        PLAYER_ID: {
            id: 'mopa.deco-flow.filter.player_id',
            dashboardItemFactory: DashboardItemFactories['filter.string_input'](
                {
                    createdInstanceId: 'mopa.deco-flow.filter.player_id',
                    datasourceColumnId: 'PLAYER_ID',
                }
            ),
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        INSTALL_DAY_NO: {
            id: 'mopa.deco-flow.filter.install_day_no',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'mopa.deco-flow.filter.install_day_no',
                datasourceColumnId: 'INSTALL_DAY_NO',
                splitable: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.deco-flow.filter.install_day_no']
                    .emitter,
        },
        ACTIVITY_APP_VERSION_GROUP_MINOR: {
            id: 'mopa.deco-flow.filter.activity_app_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.activity_app_version_group_minor',
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.activity_app_version_group_minor'
                ].emitter,
        },
        ACTIVITY_APP_VERSION: {
            id: 'mopa.deco-flow.filter.activity_app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.activity_app_version',
                    'ACTIVITY_APP_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.activity_app_version'
                ].emitter,
        },
        SEGMENT: {
            id: 'mopa.deco-flow.filter.segment',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'mopa.deco-flow.filter.segment',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.deco-flow.filter.segment'].emitter,
        },
        FIREBASE_FIRST_OPEN_DATE: {
            id: 'mopa.deco-flow.filter.firebase_first_open_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'mopa.deco-flow.filter.firebase_first_open_date',
                datasourceColumnId: 'FIREBASE_FIRST_OPEN_DATE',
                splitable: false,
            }),
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIREBASE_APP_STORE: {
            id: 'mopa.deco-flow.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.firebase_app_store',
                    'FIREBASE_APP_STORE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.firebase_app_store'
                ].emitter,
        },
        FIREBASE_COUNTRY_EUROPEAN_UNION: {
            id: 'mopa.deco-flow.filter.firebase_country_european_union',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.firebase_country_european_union',
                    'FIREBASE_COUNTRY_EUROPEAN_UNION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.firebase_country_european_union'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'mopa.deco-flow.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.firebase_country_tier',
                    'FIREBASE_COUNTRY_TIER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'mopa.deco-flow.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.firebase_country',
                    'FIREBASE_COUNTRY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.deco-flow.filter.firebase_country']
                    .emitter,
        },
        ATTRIBUTION_TRAFFIC_SOURCE: {
            id: 'mopa.deco-flow.filter.attribution_traffic_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.attribution_traffic_source',
                    'ATTRIBUTION_TRAFFIC_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.attribution_traffic_source'
                ].emitter,
        },
        ATTRIBUTION_NETWORK_NAME: {
            id: 'mopa.deco-flow.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.attribution_network_name',
                    'ATTRIBUTION_NETWORK_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.attribution_network_name'
                ].emitter,
        },
        ATTRIBUTION_CAMPAIGN_NAME: {
            id: 'mopa.deco-flow.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.attribution_campaign_name',
                    'ATTRIBUTION_CAMPAIGN_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.attribution_campaign_name'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
            id: 'mopa.deco-flow.filter.firebase_install_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.firebase_install_version_group_minor',
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.firebase_install_version_group_minor'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION: {
            id: 'mopa.deco-flow.filter.firebase_install_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.firebase_install_version',
                    'FIREBASE_INSTALL_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.firebase_install_version'
                ].emitter,
        },
        ETL_PAYER: {
            id: 'mopa.deco-flow.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.etl_payer',
                    'ETL_PAYER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.deco-flow.filter.etl_payer']
                    .emitter,
        },
        ETL_REVENUE_BADGE: {
            id: 'mopa.deco-flow.filter.etl_revenue_badge',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.deco-flow.filter.etl_revenue_badge',
                    'ETL_REVENUE_BADGE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.deco-flow.filter.etl_revenue_badge'
                ].emitter,
        },
    };

    readonly filterDashboardItemSections: DecoFlowFilterSection[] = [
        {
            title: 'Chart specific',
            accent: 'chart',
            includesMetricSettings: true,
            columnIds: [
                'DECO_CONTEXT_RARITY',
                'DECO_CONTEXT_SIZE',
                'DECO_CONTEXT_SET_ID',
                'DECO_CONTEXT_INSPIRATIONS',
                'RESOURCES_SOURCE',
                'RESOURCE_PAYMENT_TYPE',
            ],
        },
        {
            title: 'Activity',
            accent: 'activity',
            columnIds: [
                'ACTIVITY_DATE',
                'INSTALL_DAY_NO',
                'ACTIVITY_APP_VERSION_GROUP_MINOR',
                'ACTIVITY_APP_VERSION',
            ],
        },
        {
            title: 'Audience',
            accent: 'audience',
            columnIds: ['SEGMENT', 'PLAYER_ID'],
        },
        {
            title: 'Player context',
            accent: 'context',
            columnIds: [
                'FIREBASE_FIRST_OPEN_DATE',
                'FIREBASE_APP_STORE',
                'FIREBASE_COUNTRY_EUROPEAN_UNION',
                'FIREBASE_COUNTRY_TIER',
                'FIREBASE_COUNTRY',
                'ATTRIBUTION_TRAFFIC_SOURCE',
                'ATTRIBUTION_NETWORK_NAME',
                'ATTRIBUTION_CAMPAIGN_NAME',
                'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                'FIREBASE_INSTALL_VERSION',
                'ETL_PAYER',
                'ETL_REVENUE_BADGE',
            ],
        },
    ];

    override async ngOnInit() {
        super.ngOnInit();
        // Attach before child view initialization.
        this.attachAxis('date');
    }

    /** Point the Flow composition at one axis' loader; `attach` drops the previous subscription. */
    private attachAxis(axis: FlowAxis) {
        const isByDayNo = axis !== 'date';
        this.flowState.attach(
            this.chartDataLoaders[
                isByDayNo
                    ? 'mopa.deco-flow.by_day_no'
                    : 'mopa.deco-flow.by_date'
            ].emitter,
            {
                instanceId: 'mopa.deco-flow',
                colorMode: 'flow',
                dateKey: isByDayNo ? 'day_no' : 'date',
                xAxisSortFn: isByDayNo
                    ? (a, b) => Number(a) - Number(b)
                    : undefined,
            }
        );
    }
    accumulationDisabled = true;
    populationDisabled = true;
    percentilesDisabled = true;
    private syncSettings(context: IQueryContext) {
        const valueMode = getDecoFlowStatistic(context);
        this.accumulationDisabled = getFlowAxis(context) === 'date';
        this.populationDisabled =
            valueMode !== 'average' && valueMode !== 'percentile';
        this.percentilesDisabled = valueMode !== 'percentile';
    }

    override async changePendingQueryContextHandler(context: IQueryContext) {
        this.syncSettings(context);
        await super.changePendingQueryContextHandler(context);
    }

    override async changeAppliedQueryContextHandler(context: IQueryContext) {
        this.syncSettings(context);
        // Re-point before the loaders run, so the charts never consume the other axis' emission.
        this.attachAxis(getFlowAxis(context));
        await super.changeAppliedQueryContextHandler(context);
    }

    ngAfterViewInit() {
        this.standardDashboardInitialize();
    }

    /** Toggle a series from its breakdown row. */
    toggleSeriesFromRow(row: TableDataItem) {
        const seriesName = (row.meta as { seriesName?: string } | undefined)
            ?.seriesName;
        if (seriesName) {
            this.flowState.toggleSeries(seriesName);
        }
    }

    /** Show or hide one breakdown side. */
    setBreakdownSideVisibility(dir: 'in' | 'out', visible: boolean) {
        this.flowState.setSideSeriesVisibility(dir, visible);
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;
        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: { preset: 'last 90 days', splitAggregate: 'day' },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const firstOpenDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_FIRST_OPEN_DATE',
                state: {
                    value: { preset: 'last 90 days', splitAggregate: 'day' },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        initialContext.datasourceColumns = [activityDateIC, firstOpenDateIC];
        initialContext.settings = {
            ...initialContext.settings,
            [QueryContextSettingID.FLOW_X_AXIS]: 'date',
            [QueryContextSettingID.FLOW_ACCUMULATION]: 'daily',
            [QueryContextSettingID.FLOW_VALUE_MODE]: 'total',
            [QueryContextSettingID.FLOW_POPULATION]: 'deu',
            [QueryContextSettingID.SHOW_PERCENTILE_50]: true,
            [QueryContextSettingID.SHOW_PERCENTILE_75]: false,
            [QueryContextSettingID.SHOW_PERCENTILE_90]: false,
            [QueryContextSettingID.SHOW_PERCENTILE_99]: false,
        };
        return initialContext;
    }
}
